import { getAccessToken } from '../../auth';
import config from '../../../assets/config';

// Telemetry is fire-and-forget: a 401 from the /mt tracking endpoint must NOT drive
// session state. The tracking service can 401 for reasons unrelated to the Keycloak
// session (and a lost analytics ping is harmless), so routing it into SESSION_EXPIRED
// spuriously opened the global modal during active use (e.g. on a plan-change event).

/**
 * Mixpanel's default screen properties. A User-Agent does not carry the screen size,
 * so rest-api cannot derive these two the way it derives $os, $browser and $device —
 * the browser has to report them. `window.screen` is used rather than the viewport so
 * that in-app events stay comparable with the marketing-page events, which the Mixpanel
 * browser SDK fills from the same source.
 */
const screenProperties = () => {
  const { width, height } = window.screen ?? {};

  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? { $screen_width: width, $screen_height: height }
    : {};
};

export const useEvent = () => {
  const baseUrl = `${config.apiUrl.restServer}/v3`;

  return {
    track: (event, properties) => {
      const accessToken = getAccessToken();

      fetch(`${baseUrl}/mt/${event}`, {
        method: 'POST',
        body: JSON.stringify({ properties: { ...properties, ...screenProperties() } }),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        // Nobody awaits this, so an event fired immediately before a navigation
        // (the 2FA enrollment button redirects to Keycloak) would otherwise be
        // cancelled on unload and silently lost. Bodies here are a few hundred
        // bytes, far under the 64KB keepalive limit.
        keepalive: true,
      })
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Fired Tracking of "${event}" with`, properties);
          }
        })
        .catch(() => {
          console.log(`Failed to track ${event}`);
          console.error({ event, properties });
        });

      // Just for PoC
      if (window.ctrack) window.ctrack(event, properties);
    },
    updateUserProfile: (properties) => {
      const accessToken = getAccessToken();

      fetch(`${baseUrl}/mt/user`, {
        method: 'POST',
        body: JSON.stringify(properties),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
      })
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fired user profile updating with properties: ', properties);
          }
        })
        .catch(() => {
          console.log(`Failed to update user profile with properties: ${properties}`);
          console.error({ properties });
        });
    },
    updateUserProfileWithEmail: (properties) => {
      const accessToken = getAccessToken();

      fetch(`${baseUrl}/mt/user`, {
        method: 'PUT',
        body: JSON.stringify(properties),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
      })
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fired user profile updating with properties: ', properties);
          }
        })
        .catch(() => {
          console.log(`Failed to update user profile with properties: ${properties}`);
          console.error({ properties });
        });
    },
  };
};
